import React from 'react';
import { WashingMachineIcon, RefrigeratorIcon, TvIcon, MicrowaveIcon, BoxIcon } from 'lucide-react';
export const ServiceCoverage = () => {
  const serviceInfo = [{
    title: 'Service Activation & Start Date',
    items: [{
      question: 'When does my cover begin?',
      answer: 'Your protection begins immediately once your payment is processed. You will receive a confirmation email shortly afterwards.'
    }, {
      question: 'Do I need to take any steps to activate my plan?',
      answer: 'No, activation happens automatically. Your plan begins as soon as your purchase is complete and remains valid for the duration of your chosen plan.'
    }, {
      question: 'When does my coverage end?',
      answer: 'Your plan expires at 23:59 on the final day of your coverage period, unless you renew it beforehand.'
    }]
  }, {
    title: 'How Do I Request a Repair?',
    content: <div data-id="element-493">
          <p className="mb-4" data-id="element-494">Please contact us by:</p>
          <p className="mb-2" data-id="element-495">
            📞{' '}
            <a href="tel:08000554969" className="text-primary hover:underline" data-id="element-496">
              0800 055 4969
            </a>
          </p>
          <p className="mb-4" data-id="element-497">
            📧{' '}
            <a href="mailto:rawalraj2057@gmail.com" className="text-primary hover:underline" data-id="element-498">
              rawalraj2057@gmail.com
            </a>
          </p>
          <p data-id="element-499">
            Our team will organise your repair appointment, usually within 24 to
            48 hours.
          </p>
        </div>
  }, {
    title: "What's Included?",
    items: ['Unlimited repair visits and callouts for appliances covered during your plan period', 'All labour costs are covered', 'Replacement parts provided when available', 'Engineer appointments are generally booked within 24–48 hours after reporting a fault', 'Coverage includes mechanical and electrical faults']
  }, {
    title: "What's Not Covered?",
    items: ['Cosmetic damage or normal wear and tear of appliances', 'Faults that existed prior to your plan start date', 'Damage caused by misuse or unauthorised repairs', 'Appliances older than 10 years at the start of your plan', 'Damage due to floods, fires, or other natural disasters']
  }, {
    title: 'Service Area',
    content: <p data-id="element-500">
          We currently provide services across mainland UK postcodes only. For
          remote areas, response times may be longer. Please contact us to
          confirm if your location is covered.
        </p>
  }, {
    title: 'Response Times',
    content: <p data-id="element-501">
          We aim to schedule engineer visits within 24 to 48 hours, excluding
          weekends and public holidays.
        </p>
  }, {
    title: 'Plan Exclusions & Prohibited Use',
    content: <div data-id="element-502">
          <p className="mb-4" data-id="element-503">
            To ensure fair use and maintain service quality, the following are
            excluded from cover:
          </p>
          <ul className="list-disc pl-5 space-y-2" data-id="element-504">
            <li data-id="element-505">Damage or faults existing before your plan started</li>
            <li data-id="element-506">Damage caused by neglect, misuse, or tampering</li>
            <li data-id="element-507">Repairs unrelated to normal appliance operation</li>
            <li data-id="element-508">Any illegal or fraudulent use of our services or website</li>
            <li data-id="element-509">Appliances used contrary to manufacturer instructions</li>
          </ul>
          <p className="mt-4 font-medium" data-id="element-510">
            Please note: If any of these exclusions apply, your plan may be
            cancelled without refund or further support.
          </p>
        </div>
  }];
  const appliances = [{
    name: 'Washing Machines',
    icon: WashingMachineIcon
  }, {
    name: 'Refrigerators',
    icon: RefrigeratorIcon
  }, {
    name: 'Televisions',
    icon: TvIcon
  }, {
    name: 'Microwaves',
    icon: MicrowaveIcon
  }, {
    name: 'Dishwashers',
    icon: BoxIcon
  }, {
    name: 'Ovens',
    icon: BoxIcon
  }];
  return <div className="w-full" data-id="element-511">
      {/* Hero Section */}
      <div className="bg-primary py-16 md:py-24 w-full" data-id="element-512">
        <div className="container mx-auto px-4" data-id="element-513">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-514">
            Service Coverage
          </h1>
          <p className="text-xl text-white/90 max-w-3xl" data-id="element-515">
            Comprehensive repair and maintenance services for all your essential
            home appliances. Our coverage is designed to give you peace of mind
            and keep your home running smoothly.
          </p>
        </div>
      </div>
      {/* Appliances We Cover */}
      <section className="py-16 bg-light" data-id="element-516">
        <div className="container mx-auto px-4" data-id="element-517">
          <h2 className="text-3xl font-bold text-primary mb-12 text-center" data-id="element-518">
            Appliances We Cover
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-519">
            {appliances.map((appliance, index) => <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center" data-id="element-520">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary/10 rounded-full" data-id="element-521">
                  <appliance.icon className="text-primary" size={32} data-id="element-522" />
                </div>
                <h3 className="font-medium text-dark" data-id="element-523">{appliance.name}</h3>
              </div>)}
          </div>
        </div>
      </section>
      {/* Service Information */}
      <section className="py-16 bg-white" data-id="element-524">
        <div className="container mx-auto px-4" data-id="element-525">
          <h2 className="text-3xl font-bold text-primary mb-12 text-center" data-id="element-526">
            Our Service Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-527">
            {serviceInfo.map((section, index) => <div key={index} className="bg-light rounded-lg p-6" data-id="element-528">
                <h3 className="text-xl font-bold text-primary mb-4" data-id="element-529">
                  {section.title}
                </h3>
                {section.content && <div className="text-gray-700" data-id="element-530">{section.content}</div>}
                {section.items && Array.isArray(section.items) && typeof section.items[0] === 'string' && <ul className="list-disc pl-5 space-y-2 text-gray-700" data-id="element-531">
                      {section.items.map((item, idx) => <li key={idx} data-id="element-532">{item}</li>)}
                    </ul>}
                {section.items && Array.isArray(section.items) && typeof section.items[0] === 'object' && <div className="space-y-4" data-id="element-533">
                      {section.items.map((item, idx) => <div key={idx} className="mb-3" data-id="element-534">
                          <h4 className="font-medium text-dark mb-1" data-id="element-535">
                            {item.question}
                          </h4>
                          <p className="text-gray-600" data-id="element-536">{item.answer}</p>
                        </div>)}
                    </div>}
              </div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-primary/10" data-id="element-537">
        <div className="container mx-auto px-4 text-center" data-id="element-538">
          <h2 className="text-3xl font-bold text-primary mb-6" data-id="element-539">
            Ready to Protect Your Home Appliances?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-540">
            Choose one of our comprehensive service plans today and enjoy peace
            of mind knowing your essential appliances are protected by
            Prismedge's expert care.
          </p>
          <a href="/" className="inline-block bg-secondary hover:bg-secondary/90 text-white font-bold py-3 px-8 rounded-md text-lg transition-colors" data-id="element-541">
            View Our Service Plans
          </a>
        </div>
      </section>
    </div>;
};